import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../Authentication/AxiosInterceptor";
import { Category } from "../../../Components/Utils/SelectionPane/SelectionPane";
import { ISelectOption } from "../../../Components/Utils/MultiSelect/MultiSelect";
import { ISelectionItem } from "../../../Types/Analyse/ISelectionItem";
import { ResourceMessage, AnalyseFormat } from "../../../Types/Analyse/Message";
import { MessageType } from "../../../Types/Analyse/MessageType";
import { AnalyseChat } from "../../../Types/Analyse/AnalyseChat";
import {
  AnalyseChatData,
  AnalyseConversationFile,
  AnalyseLinkedEvidenceFile,
} from "../../../Types/Analyse/AnalyseChatData";
import { UpdateChatPrompt, UpdateChatActivities, GetArchivedChats, UnarchiveChat } from "../../../API/Analyse/Analyse";
import { AnalyseDataRequest } from "../../../Types/Paging/PagedRequest";
import { IDrawerSection } from "../../../Types/Drawer/ISectionsDrawerItem";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { RatingValue } from "../../../Components/Utils/Rating/Rating";
import { UpdateRatingRequest } from "../../../Types/UpdateRatingRequest";
import { sendMcpMessageStreaming } from "../../../API/McpChat/McpChat";
import { McpStreamEvent } from "../../../Types/McpChat/McpChat";
import { buildAnalyseInstructions } from "../../../Utils/analysePrompt";
import { FileData } from "../../../Components/Utils/FileItem/FileItem";
import { LibraryFileRef } from "../../../Types/Library/Library";
import { CurateResource } from "../../../Types/Curate/CurateResource";
import { isUserFacingProgressEvent, appendProgressStep } from "../../../Utils/mcpProgress";
import {
  clearAnalyseChatResponding,
  hasLocalAnalyseChatResponding,
  markAnalyseChatResponding,
} from "../../../Utils/analyseResponding";
import { takeCurateResourcesFromAnswer } from "../../../Utils/aiAssessment";
import { normalizeCurateResources } from "../../../Utils/curateResources";

interface AnalyseState {
  chatId: string;
  messages: ResourceMessage[];
  chats: IDrawerSection[];
  archivedChats: IDrawerSection[];
  selectedOrganisations: ISelectOption[];
  selectedDepartments: ISelectOption[];
  selectedGroups: ISelectOption[];
  selectedTemplateTypes: ISelectOption[];
  categories: Category<string>[];
  improveCategories: Category<string>[];
  developCategories: Category<string>[];
  prompts: ISelectionItem<string>[];
  loading: boolean;
  /** True while GetAnalyseChatSetup is in flight for the active chat. */
  isLoadingMessages: boolean;
  /** True while a chat delete request is in flight. */
  isDeletingChat: boolean;
  /** True while a bulk archive request is in flight. */
  isArchivingChats: boolean;
  freePrompt: string;
  selectedChat: AnalyseChat | null;
  isStreaming: boolean;
  /** Recovered mid-reply after refresh/navigation; Mia is still working server-side. */
  isAwaitingRemoteResponse: boolean;
  progressSteps: string[];
  streamingAssistantMessageId: string | null;
  lastSendFailure: { chatId: string; prompt: string } | null;
  uploadedEvidence: FileData[];
  selectedEvidenceIds: string[];
  uploadingEvidence: boolean;
}

const initialState: AnalyseState = {
  chatId: "",
  messages: [],
  chats: [],
  archivedChats: [],
  selectedOrganisations: [],
  selectedDepartments: [],
  selectedGroups: [],
  selectedTemplateTypes: [],
  categories: [],
  improveCategories: [],
  developCategories: [],
  prompts: [],
  loading: false,
  isLoadingMessages: false,
  isDeletingChat: false,
  isArchivingChats: false,
  freePrompt: "",
  selectedChat: null,
  isStreaming: false,
  isAwaitingRemoteResponse: false,
  progressSteps: [],
  streamingAssistantMessageId: null,
  lastSendFailure: null,
  uploadedEvidence: [],
  selectedEvidenceIds: [],
  uploadingEvidence: false,
};

const toolProgressLabels: Record<string, string> = {
  getuserlibraryfiles: "Getting your linked files",
  getconversationfiles: "Getting your uploaded files",
  retrievelibraryfilescontent: "Reading your evidence",
  getdiagnosticcriteriacontext: "Reviewing the diagnostic questions",
  getanalyseactivitiescontext: "Reading your selected diagnostics",
  listclientanalyseratings: "Checking past feedback",
};

const normaliseToolName = (toolName: string): string =>
  toolName.replace(/[_\-\s]/g, "").toLowerCase();

const humaniseToolName = (toolName: string): string => {
  const friendly = toolProgressLabels[normaliseToolName(toolName)];
  if (friendly) {
    return friendly;
  }

  const gerunds: Record<string, string> = {
    get: "getting",
    retrieve: "retrieving",
    fetch: "fetching",
    list: "listing",
    read: "reading",
    search: "searching",
    find: "finding",
    update: "updating",
    create: "creating",
    generate: "generating",
    check: "checking",
  };

  const words = toolName
    .replace(/[_-]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim()
    .split(" ");

  if (words[0] && gerunds[words[0]]) {
    words[0] = gerunds[words[0]];
  }

  const sentence = words.join(" ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

const streamEventToProgressText = (event: McpStreamEvent, answerLength: number): string | null => {
  if (!isUserFacingProgressEvent(event)) {
    return null;
  }

  switch (event.type) {
    case "status":
    case "activity":
      return event.text ?? null;
    case "tool":
      if (event.text) {
        return event.text;
      }
      return event.toolName ? `${humaniseToolName(event.toolName)}...` : "Working...";
    case "delta":
      return answerLength > 0 ? "Processing results..." : null;
    default:
      return null;
  }
};

export interface SendAnalyseMcpMessagePayload {
  token: string;
  clientId: string;
  chatId: string;
  freePrompt: string;
  categories: Category<string>[];
  improveCategories: Category<string>[];
  developCategories: Category<string>[];
  evidenceFiles: FileData[];
  signal?: AbortSignal;
}

export const fetchPrompts = createAsyncThunk<ISelectionItem<string>[]>(
  "analyse/fetchPrompts",
  async () => {
    const response = await axiosInstance.get<ISelectionItem<string>[]>("/GetAvailablePrompts");
    return response.data;
  }
);

export const fetchAnalyseSetup = createAsyncThunk<
  AnalyseChatData,
  {
    chatId: string;
    clientId: string;
    userSub: string;
    selectedOrgs: string[];
    selectedDeps: string[];
    selectedGroups: string[];
    /** Poll for mid-response recovery — skip loading buffer / evidence wipe. */
    quiet?: boolean;
  }
>(
  "analyse/fetchSetup",
  async ({ chatId, clientId, userSub, selectedOrgs, selectedDeps, selectedGroups }) => {
    const request: AnalyseDataRequest = {
      pageNumber: 1,
      pageSize: 10000,
      searchTerm: "",
      partnerIds: selectedOrgs,
      departmentIds: selectedDeps,
      groupIds: selectedGroups,
    };
    const response = await axiosInstance.post<AnalyseChatData>(
      `GetAnalyseChatSetup/${chatId}/${clientId}/${encodeURIComponent(userSub)}`,
      request
    );
    return response.data;
  }
);

export const updateMessageRating = createAsyncThunk<
  { messageId: string; rating?: RatingValue; feedback?: string },
  { messageId: string; chatId?: string; rating?: RatingValue; feedback?: string }
>(
  "analyse/updateMessageRating",
  async ({ messageId, chatId, rating, feedback }) => {
    const request: UpdateRatingRequest = {
      messageId,
      chatId: chatId || undefined,
      rating: rating || undefined,
      feedback: feedback || undefined,
    };
    await axiosInstance.post(`UpdateSystemMessageRating`, request);
    return { messageId, rating, feedback };
  }
);

export const updatePromptAction = createAsyncThunk<
  { chatId: string; promptId: string },
  { chatId: string; promptId: string }
>(
  "analyse/updatePrompt",
  async ({ chatId, promptId }) => {
    await UpdateChatPrompt(chatId, promptId);
    return { chatId, promptId };
  }
);

export const updateActivitiesAction = createAsyncThunk<
  string[],
  { chatId: string; activityIds: string[] }
>(
  "analyse/updateActivities",
  async ({ chatId, activityIds }) => {
    await UpdateChatActivities(chatId, activityIds);
    return activityIds;
  }
);

export const sendAnalyseMcpMessage = createAsyncThunk<
  { assistantMessageId: string; title?: string },
  SendAnalyseMcpMessagePayload
>(
  "analyse/sendMcpMessage",
  async (payload, thunkApi) => {
    const {
      token,
      clientId,
      chatId,
      freePrompt,
      categories,
      improveCategories,
      developCategories,
      evidenceFiles,
      signal,
    } = payload;

    const scope = buildAnalyseInstructions(
      categories,
      improveCategories,
      developCategories,
      evidenceFiles
    );

    const stateBefore = thunkApi.getState() as { analyse: AnalyseState };
    const currentTitle = stateBefore.analyse.selectedChat?.title?.trim() ?? "";
    const needsRename = !currentTitle || currentTitle.toLowerCase() === "new chat";
    const instructions = needsRename
      ? `${scope.instructions}

IMPORTANT — conversation naming (required this turn):
The current chat title is "${currentTitle || "New chat"}". You MUST call rename_conversation once in this turn.
Invent a concise topic title (max ~6 words) from the user's question and selected diagnostics/plans/evidence — not "Analyse chat" and not the full question.
Do not narrate the rename or mention the tool in your reply.`
      : scope.instructions;

    let answerLength = 0;
    let streamedTitle: string | undefined;

    // Batch streamed tokens into one store update per ~80ms window. Dispatching per SSE
    // frame re-renders the transcript dozens of times a second and tanks the framerate.
    const DELTA_FLUSH_MS = 80;
    let pendingDelta = "";
    let deltaFlushTimer: ReturnType<typeof setTimeout> | null = null;
    let lastProgressText: string | null = null;

    const flushPendingDelta = () => {
      deltaFlushTimer = null;
      if (!pendingDelta) return;
      const text = pendingDelta;
      pendingDelta = "";
      thunkApi.dispatch(appendAssistantDelta(text));
    };

    const queueDelta = (text: string) => {
      pendingDelta += text;
      if (deltaFlushTimer === null) {
        deltaFlushTimer = setTimeout(flushPendingDelta, DELTA_FLUSH_MS);
      }
    };

    try {
      try {
      await sendMcpMessageStreaming(
        token,
        freePrompt.trim(),
        clientId,
        (event) => {
          if (event.type === "delta" && event.text) {
            answerLength += event.text.length;
            queueDelta(event.text);
          }

          if (event.type === "tool" && (event.toolName || event.text)) {
            thunkApi.dispatch(
              appendStreamingDebugTurn({
                role: "assistant",
                kind: "functionCall",
                name: event.toolName,
                summary: event.text || (event.toolName ? `Called ${event.toolName}` : "Working..."),
              })
            );
          }

          if (event.type === "toolResult" && event.text) {
            thunkApi.dispatch(
              appendStreamingDebugTurn({
                role: "tool",
                kind: "reasoning",
                name: event.toolName,
                summary: event.text,
              })
            );
          }

          if (event.type === "title" && event.text) {
            streamedTitle = event.text;
            thunkApi.dispatch(applyChatTitle({ chatId, title: event.text }));
          }

          if (event.type === "summary" && event.text) {
            thunkApi.dispatch(applyChatSummary({ chatId, summary: event.text }));
          }

          if (event.type === "resources" && event.items) {
            // Flush buffered tokens first so strip runs on the full streamed answer.
            if (deltaFlushTimer !== null) {
              clearTimeout(deltaFlushTimer);
              deltaFlushTimer = null;
            }
            flushPendingDelta();
            thunkApi.dispatch(
              setStreamingAssistantResources(normalizeCurateResources(event.items))
            );
          }

          if (event.type === "done") {
            if (deltaFlushTimer !== null) {
              clearTimeout(deltaFlushTimer);
              deltaFlushTimer = null;
            }
            flushPendingDelta();
            // Client-side fallback when the resources SSE event is missing/late.
            thunkApi.dispatch(finalizeStreamingAssistantCurateResources());
          }

          const progressText = streamEventToProgressText(event, answerLength);
          if (progressText && progressText !== lastProgressText) {
            lastProgressText = progressText;
            thunkApi.dispatch(pushProgressStep(progressText));
          }
        },
        // Keep conversation document id = Analyse chat id for ratings/history.
        chatId,
        // Resume/lookup key after refresh.
        chatId,
        signal,
        {
          instructions,
          mode: "Analyse",
          analyseContext: {
            activityIds: scope.activityIds,
            selectionSummary: scope.selectionSummary,
            evidenceSummary: scope.evidenceSummary,
          },
        }
      );
      } finally {
        // Flush whatever is buffered so the transcript keeps every token, even on error/abort.
        if (deltaFlushTimer !== null) {
          clearTimeout(deltaFlushTimer);
        }
        flushPendingDelta();
      }

      const state = thunkApi.getState() as { analyse: AnalyseState };
      return {
        assistantMessageId: state.analyse.streamingAssistantMessageId ?? "",
        ...(streamedTitle ? { title: streamedTitle } : {}),
      };
    } catch (error) {
      if (
        (error instanceof DOMException && error.name === "AbortError") ||
        (error instanceof Error && error.name === "AbortError")
      ) {
        return thunkApi.rejectWithValue({ cancelled: true });
      }
      const message = error instanceof Error ? error.message : "Something went wrong. Please try again.";
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const deleteChat = createAsyncThunk(
  "analyse/deleteChat",
  async (chatId: string, thunkAPI) => {
    try {
      await axiosInstance.get(`DeleteChat/${chatId}`);
      return chatId;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteAllMyChats = createAsyncThunk(
  "analyse/deleteAllMyChats",
  async (clientId: string, thunkAPI) => {
    try {
      await axiosInstance.post(`DeleteMyAnalyseChats/${clientId}`);
      return true;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const archiveAllMyChats = createAsyncThunk(
  "analyse/archiveAllMyChats",
  async (clientId: string, thunkAPI) => {
    try {
      await axiosInstance.post(`ArchiveMyAnalyseChats/${clientId}`);
      return true;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const archiveChat = createAsyncThunk(
  "analyse/archiveChat",
  async (chatId: string, thunkAPI) => {
    try {
      await axiosInstance.get(`ArchiveChat/${chatId}`);
      return chatId;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchArchivedChats = createAsyncThunk<
  IDrawerSection[],
  { clientId: string; userSub: string }
>(
  "analyse/fetchArchivedChats",
  async ({ clientId, userSub }) => {
    const response = await GetArchivedChats(clientId, userSub);
    return response;
  }
);

export const unarchiveChat = createAsyncThunk(
  "analyse/unarchiveChat",
  async (chatId: string, thunkAPI) => {
    try {
      await UnarchiveChat(chatId);
      return chatId;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const mapLinkedEvidence = (files: AnalyseLinkedEvidenceFile[]): FileData[] =>
  files.map((file) => ({
    id: file.id,
    fileName: file.fileName,
    portfolioId: file.portfolioId ?? "",
    folderId: file.folderId ?? "",
    source: "library" as const,
    linkId: file.linkId,
    isAvailable: file.isAvailable,
  }));

const mapConversationFiles = (files: AnalyseConversationFile[]): FileData[] =>
  files.map((file) => ({
    id: file.fileId,
    fileName: file.fileName,
    portfolioId: "",
    folderId: "",
    source: "uploaded" as const,
    conversationId: file.conversationId,
  }));

const dedupeById = (files: FileData[]): FileData[] => {
  const byId = new Map<string, FileData>();
  for (const file of files) {
    if (file.id) {
      byId.set(file.id, file);
    }
  }
  return Array.from(byId.values());
};

const libraryPathKey = (file: Pick<FileData, "portfolioId" | "folderId" | "fileName">) =>
  `${file.portfolioId}|${file.folderId}|${file.fileName}`;

const resetEvidenceState = (state: AnalyseState) => {
  state.uploadedEvidence = [];
  state.selectedEvidenceIds = [];
  state.uploadingEvidence = false;
};

export const AnalyseSlice = createSlice({
  name: "analyse",
  initialState,
  reducers: {
    setChatId(state, action: PayloadAction<string>) {
      if (state.chatId !== action.payload) {
        resetEvidenceState(state);
        // Drop previous chat transcript immediately so a slow/stale setup
        // response cannot flash the wrong messages, then wipe them.
        state.messages = [];
        state.selectedChat = null;
        state.isStreaming = false;
        state.isAwaitingRemoteResponse = false;
        state.streamingAssistantMessageId = null;
        state.progressSteps = [];
        state.isLoadingMessages = true;
      }
      state.chatId = action.payload;
    },
    setAwaitingRemoteResponse(state, action: PayloadAction<boolean>) {
      state.isAwaitingRemoteResponse = action.payload;
      if (action.payload && state.progressSteps.length === 0) {
        state.progressSteps = ["Mia is still responding…"];
      }
      if (!action.payload && !state.isStreaming) {
        state.progressSteps = [];
      }
    },
    setFreePrompt(state, action: PayloadAction<string>) {
      state.freePrompt = action.payload;
    },
    setSelectedOrganisations(state, action: PayloadAction<ISelectOption[]>) {
      state.selectedOrganisations = action.payload;
    },
    setSelectedDepartments(state, action: PayloadAction<ISelectOption[]>) {
      state.selectedDepartments = action.payload;
    },
    setSelectedGroups(state, action: PayloadAction<ISelectOption[]>) {
      state.selectedGroups = action.payload;
    },
    setSelectedTemplateTypes(state, action: PayloadAction<ISelectOption[]>) {
      state.selectedTemplateTypes = action.payload;
    },
    changeCategory(state, action: PayloadAction<Category<string>>) {
      state.categories = state.categories.map((cat) =>
        cat.title === action.payload.title ? { ...action.payload } : cat
      );
    },
    changeImproveCategory(state, action: PayloadAction<Category<string>>) {
      state.improveCategories = state.improveCategories.map((cat) =>
        cat.title === action.payload.title ? { ...action.payload } : cat
      );
    },
    changeDevelopCategory(state, action: PayloadAction<Category<string>>) {
      state.developCategories = state.developCategories.map((cat) =>
        cat.title === action.payload.title ? { ...action.payload } : cat
      );
    },
    setSelectedEvidenceIds(state, action: PayloadAction<string[]>) {
      state.selectedEvidenceIds = action.payload;
    },
    selectEvidenceIds(state, action: PayloadAction<string[]>) {
      state.selectedEvidenceIds = Array.from(
        new Set([...state.selectedEvidenceIds, ...action.payload])
      );
    },
    removeEvidenceFile(state, action: PayloadAction<string>) {
      state.uploadedEvidence = state.uploadedEvidence.filter((file) => file.id !== action.payload);
      state.selectedEvidenceIds = state.selectedEvidenceIds.filter((id) => id !== action.payload);
    },
    swapEvidenceToLibrary(
      state,
      action: PayloadAction<{ uploadedFileId: string; library: FileData; wasSelected: boolean }[]>
    ) {
      for (const item of action.payload) {
        state.uploadedEvidence = state.uploadedEvidence.filter((file) => file.id !== item.uploadedFileId);
        state.selectedEvidenceIds = state.selectedEvidenceIds.filter((id) => id !== item.uploadedFileId);
        if (item.library.id && !state.uploadedEvidence.some((file) => file.id === item.library.id)) {
          state.uploadedEvidence.push(item.library);
        }
        if (item.wasSelected && item.library.id && !state.selectedEvidenceIds.includes(item.library.id)) {
          state.selectedEvidenceIds.push(item.library.id);
        }
      }
    },
    /**
     * Rebuild chat evidence from the server. Library selection carries over by
     * portfolio/folder/file path because optimistic ids can differ from server ids.
     */
    replaceChatEvidence(
      state,
      action: PayloadAction<{
        chatId: string;
        linkedEvidence: AnalyseLinkedEvidenceFile[];
        conversationFiles: AnalyseConversationFile[];
      }>
    ) {
      if (action.payload.chatId !== state.chatId) return;

      const selected = new Set(state.selectedEvidenceIds);
      const selectedLibraryPaths = new Set(
        state.uploadedEvidence
          .filter((file) => file.source === "library" && selected.has(file.id))
          .map(libraryPathKey)
      );

      const library = mapLinkedEvidence(action.payload.linkedEvidence);
      const uploads = mapConversationFiles(action.payload.conversationFiles);
      state.uploadedEvidence = dedupeById([...library, ...uploads]);

      const known = new Set(state.uploadedEvidence.map((file) => file.id));
      state.selectedEvidenceIds = Array.from(
        new Set([
          ...uploads.filter((file) => selected.has(file.id)).map((file) => file.id),
          ...library
            .filter((file) => selected.has(file.id) || selectedLibraryPaths.has(libraryPathKey(file)))
            .map((file) => file.id),
        ])
      ).filter((id) => known.has(id));
    },
    addUploadedEvidence(state, action: PayloadAction<FileData>) {
      if (!state.uploadedEvidence.some((file) => file.id === action.payload.id)) {
        state.uploadedEvidence.push(action.payload);
      }
    },
    addLibraryEvidenceRefs(state, action: PayloadAction<LibraryFileRef[]>) {
      for (const ref of action.payload) {
        // MCP retrieve_library_files_content resolves Cosmos file.Id GUIDs, not path composites.
        const id =
          ref.fileId?.trim() ||
          `${ref.portfolioId}|${ref.folderId}|${ref.fileName}`;
        if (!state.uploadedEvidence.some((file) => file.id === id)) {
          state.uploadedEvidence.push({
            id,
            fileName: ref.fileName,
            portfolioId: ref.portfolioId,
            folderId: ref.folderId,
            source: "library",
          });
        }
      }
    },
    setUploadingEvidence(state, action: PayloadAction<boolean>) {
      state.uploadingEvidence = action.payload;
    },
    appendAssistantDelta(state, action: PayloadAction<string>) {
      const targetId = state.streamingAssistantMessageId;
      if (!targetId) return;

      const message = state.messages.find((msg) => msg.id === targetId);
      if (message) {
        message.content += action.payload;
      }
    },
    setStreamingAssistantResources(
      state,
      action: PayloadAction<CurateResource[]>
    ) {
      const targetId = state.streamingAssistantMessageId;
      if (!targetId) return;

      const message = state.messages.find((msg) => msg.id === targetId);
      if (!message) return;

      message.resources = action.payload;
      const { cleanText } = takeCurateResourcesFromAnswer(message.content);
      message.content = cleanText;
    },
    /**
     * Strip the curate fence from the streaming reply. Tiles only ever come from the SSE
     * event, which is access-checked; the fence can name resources the client cannot see.
     */
    finalizeStreamingAssistantCurateResources(state) {
      const targetId = state.streamingAssistantMessageId;
      if (!targetId) return;

      const message = state.messages.find((msg) => msg.id === targetId);
      if (!message) return;

      message.content = takeCurateResourcesFromAnswer(message.content).cleanText;
    },
    appendStreamingDebugTurn(
      state,
      action: PayloadAction<{
        role: string;
        kind: string;
        name?: string;
        summary: string;
        messageId?: string;
      }>
    ) {
      const targetId = state.streamingAssistantMessageId;
      if (!targetId) return;

      const message = state.messages.find((msg) => msg.id === targetId);
      if (!message) return;

      message.debugTurns = [
        ...(message.debugTurns ?? []),
        {
          role: action.payload.role,
          kind: action.payload.kind,
          name: action.payload.name,
          summary: action.payload.summary,
          messageId: action.payload.messageId,
        },
      ];
    },
    pushProgressStep(state, action: PayloadAction<string>) {
      appendProgressStep(state.progressSteps, action.payload);
    },
    clearProgress(state) {
      state.progressSteps = [];
    },
    applyChatTitle(state, action: PayloadAction<{ chatId: string; title: string }>) {
      const nextTitle = action.payload.title.trim();
      if (!nextTitle) return;

      const matchesChat = (id: string | undefined) =>
        !!id && (id === action.payload.chatId || id === state.chatId || id === state.selectedChat?.id);

      if (state.selectedChat && matchesChat(state.selectedChat.id)) {
        state.selectedChat = { ...state.selectedChat, title: nextTitle };
      }

      let found = false;
      state.chats = state.chats.map((chat) => {
        if (!matchesChat(chat.id)) {
          return chat;
        }

        found = true;
        return {
          ...chat,
          title: nextTitle,
          items: (chat.items ?? []).map((item) =>
            item.id === chat.id ? { ...item, title: nextTitle } : item
          ),
        };
      });

      if (!found) {
        state.archivedChats = state.archivedChats.map((chat) => {
          if (!matchesChat(chat.id)) {
            return chat;
          }

          found = true;
          return {
            ...chat,
            title: nextTitle,
            items: (chat.items ?? []).map((item) =>
              item.id === chat.id ? { ...item, title: nextTitle } : item
            ),
          };
        });
      }

      if (!found && action.payload.chatId) {
        state.chats.unshift({
          id: action.payload.chatId,
          title: nextTitle,
          status: "Not started",
          items: [
            {
              id: action.payload.chatId,
              title: nextTitle,
              url: `/chat/${action.payload.chatId}`,
              checked: false,
            },
          ],
        });
      }
    },
    applyChatSummary(state, action: PayloadAction<{ chatId: string; summary: string }>) {
      const nextSummary = action.payload.summary.trim();
      if (!nextSummary) return;

      const matchesChat = (id: string | undefined) =>
        !!id && (id === action.payload.chatId || id === state.chatId || id === state.selectedChat?.id);

      if (state.selectedChat && matchesChat(state.selectedChat.id)) {
        state.selectedChat = { ...state.selectedChat, chatSummary: nextSummary };
      }

      state.chats = state.chats.map((chat) =>
        matchesChat(chat.id) ? { ...chat, chatSummary: nextSummary } : chat
      );
      state.archivedChats = state.archivedChats.map((chat) =>
        matchesChat(chat.id) ? { ...chat, chatSummary: nextSummary } : chat
      );
    },
    dismissFailedTurn(state) {
      const lastAssistantIndex = [...state.messages]
        .map((msg, index) => ({ msg, index }))
        .reverse()
        .find(({ msg }) => msg.type === MessageType.System && (!!msg.error || !!msg.cancelled))
        ?.index;
      if (lastAssistantIndex !== undefined) {
        const start =
          lastAssistantIndex > 0 && state.messages[lastAssistantIndex - 1].type === MessageType.User
            ? lastAssistantIndex - 1
            : lastAssistantIndex;
        state.messages.splice(start, lastAssistantIndex - start + 1);
      }
      state.lastSendFailure = null;
      state.streamingAssistantMessageId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPrompts.fulfilled, (state, action) => {
      state.prompts = action.payload;
    });
    builder.addCase(fetchAnalyseSetup.pending, (state, action) => {
      if (action.meta.arg.quiet) {
        return;
      }
      if (action.meta.arg.chatId === state.chatId || !state.chatId) {
        state.isLoadingMessages = true;
      }
    });
    builder.addCase(fetchAnalyseSetup.rejected, (state, action) => {
      if (action.meta.arg.quiet) {
        return;
      }
      if (action.meta.arg.chatId === state.chatId) {
        state.isLoadingMessages = false;
      }
    });
    builder.addCase(fetchAnalyseSetup.fulfilled, (state, action) => {
      // Ignore out-of-order responses from a previous chatId (common when
      // New Chat / client bootstrap races with opening an existing chat).
      if (action.meta.arg.chatId !== state.chatId) {
        return;
      }

      const data = action.payload;
      const quiet = !!action.meta.arg.quiet;

      if (!quiet) {
        state.uploadedEvidence = dedupeById([
          ...mapLinkedEvidence(data.linkedEvidence ?? []),
          ...mapConversationFiles(data.conversationFiles ?? []),
        ]);
        state.selectedEvidenceIds = state.uploadedEvidence.map((file) => file.id);
        state.uploadingEvidence = false;
      }
      const selectedIds = new Set(data.chatInfo?.selectedActivityIds ?? []);
      const applySelected = (cats: Category<string>[] | undefined) =>
        (cats ?? []).map((cat) => ({
          ...cat,
          data: (cat.data ?? []).map((item) => ({
            ...item,
            selected: selectedIds.has(item.id) || !!item.selected,
          })),
        }));

      state.categories = applySelected(data.activityIdentities);
      state.improveCategories = applySelected(data.improveActivityIdentities);
      state.developCategories = applySelected(data.developActivityIdentities);
      state.messages = (data.chatMessages ?? []).map((message) => {
        const raw = message as ResourceMessage & { dateMade?: string | Date };
        return {
          ...message,
          date: message.date
            ? new Date(message.date)
            : raw.dateMade
              ? new Date(raw.dateMade)
              : new Date(),
          resources: message.resources ?? [],
          format: message.format ?? AnalyseFormat.Text,
          debugTurns: message.debugTurns ?? [],
        };
      });
      state.selectedChat = data.chatInfo;
      if (!quiet) {
        state.isLoadingMessages = false;
      }

      const serverResponding = !!data.chatInfo?.isResponding;
      const localResponding = hasLocalAnalyseChatResponding(action.meta.arg.chatId);
      // On first load after refresh, local sticky flag covers the race before Cosmos isResponding is set.
      // Quiet polls trust the server so a finished reply clears the UI.
      const stillResponding = serverResponding || (!quiet && localResponding);

      if (stillResponding && !state.isStreaming) {
        state.isAwaitingRemoteResponse = true;
        if (state.progressSteps.length === 0) {
          state.progressSteps = ["Mia is still responding…"];
        }
        const partial = data.chatInfo?.partialAnswer?.trim();
        if (partial) {
          const last = state.messages[state.messages.length - 1];
          if (last && last.type === MessageType.System) {
            last.content = partial;
          } else {
            state.messages.push({
              id: `partial-${action.meta.arg.chatId}`,
              chatId: action.meta.arg.chatId,
              date: new Date(),
              type: MessageType.System,
              content: partial,
              resources: [],
              format: AnalyseFormat.Text,
              debugTurns: [],
            });
          }
        }
      } else if (!stillResponding) {
        state.isAwaitingRemoteResponse = false;
        clearAnalyseChatResponding(action.meta.arg.chatId);
        if (!state.isStreaming) {
          state.progressSteps = [];
        }
      }

      state.chats = (data.chatHistory ?? []).map((chat) =>
        chat.id === data.chatInfo?.id && data.chatInfo?.chatSummary && !chat.chatSummary
          ? { ...chat, chatSummary: data.chatInfo.chatSummary }
          : chat
      );

      const currentId = data.chatInfo?.id;
      const isArchived =
        !!data.chatInfo?.archived ||
        (!!currentId && state.archivedChats.some((chat) => chat.id === currentId));
      if (currentId && !isArchived && !state.chats.some((c) => c.id === currentId)) {
        state.chats.unshift({
          id: currentId,
          title: data.chatInfo.title,
          status: "Complete",
          items: [],
          chatSummary: data.chatInfo.chatSummary,
        });
      }
    });
    builder.addCase(updatePromptAction.fulfilled, (state, action) => {
      if (!state.selectedChat) return;

      state.selectedChat.promptId = action.payload.promptId;

      if (!state.freePrompt.trim()) {
        state.freePrompt =
          state.prompts.find((p) => p.id === action.payload.promptId)?.value || "";
      }

      state.chats = state.chats.map((chat) =>
        chat.id === state.selectedChat?.id
          ? { ...chat, title: state.selectedChat?.title ?? chat.title }
          : chat
      );
    });
    builder.addCase(updateActivitiesAction.fulfilled, () => {
      // no-op
    });
    builder.addCase(sendAnalyseMcpMessage.pending, (state, action) => {
      const { chatId, freePrompt } = action.meta.arg;
      const userMessageId = uuidv4();
      const assistantMessageId = uuidv4();

      markAnalyseChatResponding(chatId);
      state.isStreaming = true;
      state.isAwaitingRemoteResponse = false;
      state.loading = true;
      state.progressSteps = ["Preparing..."];
      state.streamingAssistantMessageId = assistantMessageId;
      state.freePrompt = "";
      state.lastSendFailure = null;

      state.messages.push({
        id: userMessageId,
        chatId,
        date: new Date(),
        type: MessageType.User,
        content: freePrompt,
        resources: [],
        format: AnalyseFormat.Text,
      });

      state.messages.push({
        id: assistantMessageId,
        chatId,
        date: new Date(),
        type: MessageType.System,
        content: "",
        resources: [],
        format: AnalyseFormat.Text,
        debugTurns: [],
      });
    });
    builder.addCase(sendAnalyseMcpMessage.fulfilled, (state, action) => {
      clearAnalyseChatResponding(action.meta.arg.chatId);
      state.isStreaming = false;
      state.isAwaitingRemoteResponse = false;
      state.loading = false;
      state.progressSteps = [];
      state.streamingAssistantMessageId = null;

      const nextTitle = action.payload.title?.trim();
      if (nextTitle) {
        if (state.selectedChat) {
          state.selectedChat = { ...state.selectedChat, title: nextTitle };
        }
        state.chats = state.chats.map((chat) =>
          chat.id === state.selectedChat?.id || chat.id === action.meta.arg.chatId
            ? { ...chat, title: nextTitle }
            : chat
        );
      }
    });
    builder.addCase(sendAnalyseMcpMessage.rejected, (state, action) => {
      clearAnalyseChatResponding(action.meta.arg.chatId);
      state.isStreaming = false;
      state.isAwaitingRemoteResponse = false;
      state.loading = false;
      state.progressSteps = [];

      const cancelled =
        typeof action.payload === "object" &&
        action.payload !== null &&
        "cancelled" in action.payload &&
        !!(action.payload as { cancelled?: boolean }).cancelled;

      const errorMessage = cancelled
        ? undefined
        : typeof action.payload === "string"
          ? action.payload
          : "Failed to respond to message";

      const targetId = state.streamingAssistantMessageId;
      const message = targetId ? state.messages.find((msg) => msg.id === targetId) : undefined;
      if (message) {
        if (cancelled) {
          message.cancelled = true;
        } else {
          message.error = errorMessage;
        }
      }
      state.streamingAssistantMessageId = null;
      state.lastSendFailure = cancelled
        ? null
        : { chatId: action.meta.arg.chatId, prompt: action.meta.arg.freePrompt };
    });
    builder.addCase(deleteChat.pending, (state) => {
      state.isDeletingChat = true;
    });
    builder.addCase(deleteChat.fulfilled, (state, action) => {
      state.isDeletingChat = false;
      state.chats = state.chats.filter((chat) => chat.id !== action.payload);
      clearAnalyseChatResponding(action.payload);
      toast.success("Chat deleted successfully");
    });
    builder.addCase(deleteChat.rejected, (state) => {
      state.isDeletingChat = false;
      toast.error("Failed to delete chat");
    });
    builder.addCase(deleteAllMyChats.pending, (state) => {
      state.isDeletingChat = true;
    });
    builder.addCase(deleteAllMyChats.fulfilled, (state) => {
      state.isDeletingChat = false;
      for (const chat of [...state.chats, ...state.archivedChats]) {
        clearAnalyseChatResponding(chat.id);
      }
      if (state.chatId) {
        clearAnalyseChatResponding(state.chatId);
      }
      state.chats = [];
      state.archivedChats = [];
      state.messages = [];
      state.selectedChat = null;
      state.chatId = "";
      state.isStreaming = false;
      state.isAwaitingRemoteResponse = false;
      state.progressSteps = [];
      state.streamingAssistantMessageId = null;
      state.lastSendFailure = null;
      resetEvidenceState(state);
      toast.success("All chats deleted");
    });
    builder.addCase(deleteAllMyChats.rejected, (state) => {
      state.isDeletingChat = false;
      toast.error("Failed to delete chats");
    });
    builder.addCase(archiveAllMyChats.pending, (state) => {
      state.isArchivingChats = true;
    });
    builder.addCase(archiveAllMyChats.fulfilled, (state) => {
      state.isArchivingChats = false;
      if (state.chats.length > 0) {
        state.archivedChats = [...state.archivedChats, ...state.chats];
        state.chats = [];
      }
      state.messages = [];
      state.selectedChat = null;
      state.chatId = "";
      state.isStreaming = false;
      state.isAwaitingRemoteResponse = false;
      state.progressSteps = [];
      state.streamingAssistantMessageId = null;
      state.lastSendFailure = null;
      resetEvidenceState(state);
      toast.success("All chats archived");
    });
    builder.addCase(archiveAllMyChats.rejected, (state) => {
      state.isArchivingChats = false;
      toast.error("Failed to archive chats");
    });
    builder.addCase(archiveChat.fulfilled, (state, action) => {
      const archivedChat = state.chats.find((chat) => chat.id === action.payload);
      if (archivedChat) {
        state.chats = state.chats.filter((chat) => chat.id !== action.payload);
        state.archivedChats.push(archivedChat);
      }
      toast.success("Chat archived successfully");
    });
    builder.addCase(archiveChat.rejected, () => {
      toast.error("Failed to archive chat");
    });
    builder.addCase(fetchArchivedChats.fulfilled, (state, action) => {
      state.archivedChats = action.payload;
    });
    builder.addCase(fetchArchivedChats.rejected, () => {
      toast.error("Failed to fetch archived chats");
    });
    builder.addCase(unarchiveChat.fulfilled, (state, action) => {
      const unarchivedChat = state.archivedChats.find((chat) => chat.id === action.payload);
      if (unarchivedChat) {
        state.archivedChats = state.archivedChats.filter((chat) => chat.id !== action.payload);
        state.chats.push(unarchivedChat);
      }
      toast.success("Chat unarchived successfully");
    });
    builder.addCase(unarchiveChat.rejected, () => {
      toast.error("Failed to unarchive chat");
    });
    builder.addCase(updateMessageRating.pending, (state, action) => {
      const { messageId, rating, feedback } = action.meta.arg;
      const message = state.messages.find((msg) => msg.id === messageId);
      if (message) {
        message.rating = rating;
        if (feedback !== undefined) {
          message.feedback = feedback;
        }
      }
    });
    builder.addCase(updateMessageRating.fulfilled, (state, action) => {
      const { messageId, rating, feedback } = action.payload;
      const message = state.messages.find((msg) => msg.id === messageId);
      if (message) {
        message.rating = rating;
        if (feedback !== undefined) {
          message.feedback = feedback;
        }
      }
    });
    builder.addCase(updateMessageRating.rejected, () => {
      toast.error("Failed to save rating");
    });
  },
});

export const {
  setChatId,
  setAwaitingRemoteResponse,
  setFreePrompt,
  setSelectedOrganisations,
  setSelectedDepartments,
  setSelectedGroups,
  setSelectedTemplateTypes,
  changeCategory,
  changeImproveCategory,
  changeDevelopCategory,
  setSelectedEvidenceIds,
  selectEvidenceIds,
  removeEvidenceFile,
  swapEvidenceToLibrary,
  replaceChatEvidence,
  addUploadedEvidence,
  addLibraryEvidenceRefs,
  setUploadingEvidence,
  appendAssistantDelta,
  setStreamingAssistantResources,
  finalizeStreamingAssistantCurateResources,
  appendStreamingDebugTurn,
  pushProgressStep,
  clearProgress,
  applyChatTitle,
  applyChatSummary,
  dismissFailedTurn,
} = AnalyseSlice.actions;

export default AnalyseSlice.reducer;
